// Libs
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2 } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
// Others
import { ModeActionType } from '~/utils/type/common';
import { IFormReply, IReview } from '~/utils/interface/review';
import { useAppDispatch } from '~/utils/redux/hooks';
import { defaultValuesForm, schema } from './helper';
import { createReply } from '~/thunks/review/reviewThunk';
import { DEFAULT_NUMBER_ZERO } from '~/utils/constants/common';
// Styles, images, icons

type Props = {
  onClose: () => void;
  mode: ModeActionType;
  data?: IReview;
};

const FormReply = (props: Props) => {
  //#region Destructuring Props
  const { onClose, mode, data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/review']);
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormReply>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!data?.reply || data.reply.length === DEFAULT_NUMBER_ZERO) return;

    reset({
      content: data.reply?.[DEFAULT_NUMBER_ZERO].content,
    });
  }, [data?.reply, reset]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseFormReply = () => {
    onClose();
    reset(defaultValuesForm);
  };

  const handleSubmitFormReply = (values: IFormReply) => {
    if (data?.id) {
      setIsLoading(true);
      const { content } = values;
      const body: IFormReply = {
        reviewId: data.id,
        ...(content ? { content: content } : {}),
      };
      dispatch(createReply(body))
        .unwrap()
        .then((res) => {
          handleCloseFormReply();
        })
        .catch((err) => {})
        .finally(() => setIsLoading(false));
    }
  };
  //#endregion Handle Function

  return (
    <Dialog id='formReplyComponent' open={true} onClose={handleCloseFormReply} fullWidth maxWidth={'md'}>
      <Box component={'form'} onSubmit={handleSubmit(handleSubmitFormReply)}>
        <DialogTitle>{t('form_reply_title')}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 size={12}>
              <Controller
                name='content'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInput
                    label={t('form_reply_content_label')}
                    multiline
                    height={'auto'}
                    rows={8}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.content?.message}
                  />
                )}
              />
            </Grid2>
          </Grid2>

          {isLoading && <Loading />}
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={handleCloseFormReply}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          {mode !== 'view' && (
            <Button variant='contained' type='submit'>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FormReply;
